import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { Box, Users, CheckCircle, AlertCircle, Activity } from 'lucide-react';
import { theme } from '@styles/theme';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@components/index';
import { usePrototypes, useUsers, useHealthSummary } from '@hooks/index';
const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
`;
const StatCard = styled(Card) `
  padding: ${theme.spacing.lg};
`;
const StatHeader = styled.div `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;
const StatIcon = styled.div `
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StatValue = styled.div `
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
`;
const StatLabel = styled.div `
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;
const SectionTitle = styled.h2 `
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;
const RecentList = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;
const RecentItem = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
`;
const RecentItemName = styled.span `
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;
const RecentItemMeta = styled.span `
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
`;
const LoadingContainer = styled.div `
  display: flex;
  justify-content: center;
  padding: ${theme.spacing.xl};
`;
const EmptyState = styled.div `
  text-align: center;
  padding: ${theme.spacing.lg};
  color: ${theme.colors.text.muted};
  font-size: ${theme.typography.fontSize.sm};
`;
const HealthWidgetGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;
const HealthStat = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  border-left: 3px solid ${({ $color }) => $color};
`;
const HealthStatValue = styled.span `
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
`;
const HealthStatLabel = styled.span `
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
const SectionRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;
export function AdminDashboard() {
    const { prototypes, isLoading: prototypesLoading } = usePrototypes();
    const { users, isLoading: usersLoading } = useUsers();
    const { healthSummary, isLoading: healthLoading } = useHealthSummary();
    const isLoading = prototypesLoading || usersLoading || healthLoading;
    if (isLoading) {
        return (_jsx(LoadingContainer, { children: _jsx(Spinner, { size: "lg" }) }));
    }
    const deployedCount = prototypes.filter((p) => p.status === 'deployed').length;
    const failedCount = prototypes.filter((p) => p.status === 'failed').length;
    const activeUsersCount = users.filter((u) => u.isActive).length;
    const recentPrototypes = [...prototypes]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 5);
    return (_jsxs(Container, { children: [_jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx(StatHeader, { children: _jsx(StatIcon, { "$color": theme.colors.accent.primary, children: _jsx(Box, { size: 20 }) }) }), _jsx(StatValue, { children: prototypes.length }), _jsx(StatLabel, { children: "Total Prototypes" })] }), _jsxs(StatCard, { children: [_jsx(StatHeader, { children: _jsx(StatIcon, { "$color": theme.colors.status.success, children: _jsx(CheckCircle, { size: 20 }) }) }), _jsx(StatValue, { children: deployedCount }), _jsx(StatLabel, { children: "Deployed" })] }), _jsxs(StatCard, { children: [_jsx(StatHeader, { children: _jsx(StatIcon, { "$color": theme.colors.status.error, children: _jsx(AlertCircle, { size: 20 }) }) }), _jsx(StatValue, { children: failedCount }), _jsx(StatLabel, { children: "Failed" })] }), _jsxs(StatCard, { children: [_jsx(StatHeader, { children: _jsx(StatIcon, { "$color": theme.colors.status.info, children: _jsx(Users, { size: 20 }) }) }), _jsx(StatValue, { children: activeUsersCount }), _jsx(StatLabel, { children: "Active Users" })] })] }), _jsxs(SectionRow, { children: [_jsxs("div", { children: [_jsx(SectionTitle, { children: "Health Summary" }), _jsxs(Card, { padding: "none", children: [_jsx(CardHeader, { children: _jsxs(CardTitle, { children: [_jsx(Activity, { size: 18, style: { marginRight: '8px', verticalAlign: 'middle' } }), "Deployed Prototype Health"] }) }), _jsx(CardContent, { children: healthSummary ? (_jsxs(HealthWidgetGrid, { children: [_jsxs(HealthStat, { "$color": theme.colors.status.info, children: [_jsx(HealthStatValue, { children: healthSummary.totalDeployed }), _jsx(HealthStatLabel, { children: "Total" })] }), _jsxs(HealthStat, { "$color": theme.colors.status.success, children: [_jsx(HealthStatValue, { children: healthSummary.healthy }), _jsx(HealthStatLabel, { children: "Healthy" })] }), _jsxs(HealthStat, { "$color": theme.colors.status.error, children: [_jsx(HealthStatValue, { children: healthSummary.unhealthy }), _jsx(HealthStatLabel, { children: "Unhealthy" })] }), _jsxs(HealthStat, { "$color": theme.colors.status.warning, children: [_jsx(HealthStatValue, { children: healthSummary.unknown }), _jsx(HealthStatLabel, { children: "Unknown" })] })] })) : (_jsx(EmptyState, { children: "No health data available" })) })] })] }), _jsxs("div", { children: [_jsx(SectionTitle, { children: "Recent Prototypes" }), _jsx(Card, { children: _jsx(CardContent, { children: recentPrototypes.length === 0 ? (_jsx(EmptyState, { children: "No prototypes registered yet" })) : (_jsx(RecentList, { children: recentPrototypes.map((prototype) => (_jsxs(RecentItem, { children: [_jsx(RecentItemName, { children: prototype.name }), _jsx(RecentItemMeta, { children: prototype.status })] }, prototype.id))) })) }) })] })] })] }));
}
